import {
  getDefaultTimezone,
  transformOfferToFulfillmentWindowFormValue,
  FulfillmentChannel,
  getFulfillmentChannelInitialValues,
  getMinimumPurchaseConstraintInitialValues,
  getPaymentMethodInitialValues,
  getRequiresActivationWithLimitsInitialValues,
  RequiresActivationConstraintWithLimitsFormValues,
} from '@occc/feat-constraints';
import {
  Constraint_Purchase_Threshold_Counting_Attribute,
  EarningMechanismProductDiscountFragment,
  EarningMechanismTransactionDiscountFragment,
  EarningMechanismVarietyDiscountFragment,
  OfferFragment,
  ProductGroupFragment,
} from '@occc/graphql';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import { centsToDollars } from '@occc-app/lib/currency';
import { LimitTimePeriod, getAwardCountInitialValues } from '@occc/feat-limits';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';
import { RollbackStackingConfig } from '@occc-app/dataAccessLayer/chain/types';

import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import { getOfferFulfillmentPriorityInitialValues } from '@occc-app/offerHelpers/utils/getOfferFulfillmentPriorityInitialValues';
import { transformOfferScanDataConfigurationToInitialValues } from '@occc-app/offerHelpers/utils/transformOfferScanDataConfigurationToInitialValues';
import {
  ProductDiscountAwardType,
  PromotionConfigureFormInitialValues,
  TransactionDiscountAwardType,
  VarietyDiscountAwardType,
  VarietyDiscountFormValues,
  PromotionType,
} from '../../../types';
import { transformCustomData } from '../../../../CampaignComponents/components/CustomData/utils';

export const getOfferConfigureFormInitialValues = (): PromotionConfigureFormInitialValues =>
  ({
    offerType: null,
    earningMechanism: {},
    transactionDiscount: {
      awardType: TransactionDiscountAwardType.PercentDiscount,
    },
    productDiscount: {
      awardType: ProductDiscountAwardType.Free,
    },
    varietyDiscount: {
      awardType: VarietyDiscountAwardType.TransactionDiscount,
      productDiscount: {
        awardType: ProductDiscountAwardType.Free,
      },
      purchases: [{}],
    },
    fulfillmentWindow: {
      isEnabled: false,
      timezone: getDefaultTimezone(),
    },
    minimumPurchaseConstraints: {
      isEnabled: false,
      items: [{ countProductsBy: Constraint_Purchase_Threshold_Counting_Attribute.Quantity }],
    },
    awardCount: {
      isEnabled: false,
      global: {
        isEnabled: false,
        timePeriod: LimitTimePeriod.Total,
      },
      membership: {
        isEnabled: false,
        timePeriod: LimitTimePeriod.Total,
      },
      transaction: {
        isEnabled: false,
      },
      transactionFrequency: {
        isEnabled: false,
        rows: [
          {
            limit: 1,
            timePeriod: LimitTimePeriod.Day,
          },
        ],
      },
    },
    paymentMethodConstraint: {
      isEnabled: false,
    },
    fulfillmentChannelConstraint: {
      isEnabled: false,
      channel: FulfillmentChannel.OrderAheadOnly,
    },
    stackable: false,
    activation: {
      isEnabled: false,
      global: {
        isEnabled: false,
        timePeriod: LimitTimePeriod.Total,
      },
      membership: {
        isEnabled: false,
        timePeriod: LimitTimePeriod.Total,
      },
      requiresActivation: {
        isMustFulfillWithinEnabled: false,
        allowMultipleFulfillmentsOnActivation: false,
      },
    },
    scanDataConfiguration: {
      isEnabled: false,
      fundingSource: '',
      description: '',
      discountAllocation: undefined,
    },
  } as PromotionConfigureFormInitialValues);

const getActivationInitialValues = (
  offer: OfferFragment,
  defaultValues?: PromotionConfigureFormInitialValues['activation'],
): RequiresActivationConstraintWithLimitsFormValues =>
  getRequiresActivationWithLimitsInitialValues(
    offer,
    defaultValues ?? getOfferConfigureFormInitialValues().activation,
  );

const getAwardCountSchemaInitialValues = (
  offer: OfferFragment,
  defaultValues?: PromotionConfigureFormInitialValues['awardCount'],
): NonNullable<PromotionConfigureFormInitialValues['awardCount']> =>
  getAwardCountInitialValues(offer, defaultValues, { stacked: true }) as NonNullable<
    PromotionConfigureFormInitialValues['awardCount']
  >;

const transformVarietyDiscount = (
  earningMechanism: EarningMechanismVarietyDiscountFragment,
): {
  offerType: PromotionType.VarietyDiscount;
  varietyDiscount: VarietyDiscountFormValues;
} => {
  const { varietyDiscountAward, purchases } = earningMechanism;

  if (awardHelper.isTransactionMoneyDiscountAward(varietyDiscountAward)) {
    return {
      offerType: PromotionType.VarietyDiscount,
      varietyDiscount: {
        awardType: VarietyDiscountAwardType.TransactionDiscount,
        transactionDiscount: {
          moneyDiscount: centsToDollars(varietyDiscountAward.moneyDiscount),
        },
        purchases,
      },
    };
  }

  if (awardHelper.isProductDiscountAward(varietyDiscountAward)) {
    const isFree = varietyDiscountAward.percentDiscount === 100;
    return {
      offerType: PromotionType.VarietyDiscount,
      varietyDiscount: {
        awardType: VarietyDiscountAwardType.ProductDiscount,
        productDiscount: isFree
          ? {
              awardType: ProductDiscountAwardType.Free,
              eligibleProductGroups: varietyDiscountAward.eligibleProductGroups,
              purchaseThreshold: varietyDiscountAward.purchaseThreshold,
            }
          : {
              awardType: ProductDiscountAwardType.Discount,
              eligibleProductGroups: varietyDiscountAward.eligibleProductGroups,
              purchaseThreshold: varietyDiscountAward.purchaseThreshold,
              percentDiscount: varietyDiscountAward.percentDiscount,
            },
        purchases,
      },
    };
  }

  if (awardHelper.isPriceOverrideAward(varietyDiscountAward)) {
    return {
      offerType: PromotionType.VarietyDiscount,
      varietyDiscount: {
        awardType: VarietyDiscountAwardType.PriceOverride,
        priceOverride: {
          maximumCost: centsToDollars(varietyDiscountAward.maximumCost),
        },
        purchases,
      },
    };
  }

  return {
    offerType: PromotionType.VarietyDiscount,
    varietyDiscount: {
      awardType: VarietyDiscountAwardType.ProductDiscount,
      productDiscount: {
        awardType: ProductDiscountAwardType.Free,
        eligibleProductGroups: [],
        purchaseThreshold: 0,
      },
      purchases,
    },
  };
};

const transformTransactionDiscount = (
  earningMechanism: EarningMechanismTransactionDiscountFragment,
) => ({
  offerType: PromotionType.TransactionDiscount,
  transactionDiscount: {
    ineligibleProductGroups: earningMechanism.ineligibleProductGroups || [],
    purchaseAmountThreshold: centsToDollars(earningMechanism.purchaseAmountThreshold),
    allowMultipleFulfillmentsPerProductGroupThreshold:
      earningMechanism.allowMultipleFulfillmentsPerProductGroupThreshold,
    awardType: awardHelper.isTransactionMoneyDiscountAward(
      earningMechanism.transactionDiscountAward,
    )
      ? TransactionDiscountAwardType.MoneyDiscount
      : TransactionDiscountAwardType.PercentDiscount,
    moneyDiscount: awardHelper.isTransactionMoneyDiscountAward(
      earningMechanism.transactionDiscountAward,
    )
      ? centsToDollars(earningMechanism.transactionDiscountAward.moneyDiscount)
      : undefined,
    percentDiscount: awardHelper.isTransactionPercentDiscountAward(
      earningMechanism.transactionDiscountAward,
    )
      ? earningMechanism.transactionDiscountAward.percentDiscount
      : undefined,
  },
});

const transformProductDiscount = (
  earningMechanism: EarningMechanismProductDiscountFragment,
  constraints: OfferFragment['constraints'],
) => {
  if (awardHelper.isBuyAndGetAward(earningMechanism.productDiscountAward)) {
    return {
      offerType: PromotionType.BuyAndGet,
      buyAndGet: {
        eligibleProductGroups: earningMechanism.eligibleProductGroups,
        purchaseThreshold: earningMechanism.purchaseThresholdInt,
        discountThreshold: earningMechanism.productDiscountAward.discountThreshold,
      },
    };
  }

  const hasPreAuthConstraint = constraints.some((constraint) =>
    constraintHelper.isPreAuth(constraint),
  );

  const getMoneyAmount = () => {
    if (
      awardHelper.isPriceOverrideAward(earningMechanism.productDiscountAward) &&
      earningMechanism.productDiscountAward.maximumCost !== 0
    ) {
      return centsToDollars(earningMechanism.productDiscountAward.maximumCost);
    }

    if (awardHelper.isMoneyDiscountAward(earningMechanism.productDiscountAward)) {
      return centsToDollars(earningMechanism.productDiscountAward.moneyAmount);
    }

    return undefined;
  };

  if (hasPreAuthConstraint) {
    return {
      offerType: PromotionType.FuelRollback,
      fuelRollback: {
        eligibleProductGroups: earningMechanism.eligibleProductGroups,
        moneyAmount: getMoneyAmount(),
      },
    };
  }

  const getAwardType = () => {
    if (awardHelper.isPriceOverrideAward(earningMechanism.productDiscountAward)) {
      return earningMechanism.productDiscountAward.maximumCost !== 0
        ? ProductDiscountAwardType.PriceOverride
        : ProductDiscountAwardType.Free;
    }

    return ProductDiscountAwardType.Discount;
  };

  return {
    offerType: PromotionType.ProductDiscount,
    productDiscount: {
      eligibleProductGroups: earningMechanism.eligibleProductGroups,
      purchaseThreshold: earningMechanism.purchaseThresholdInt,
      awardType: getAwardType(),
      moneyAmount: getMoneyAmount(),
      countProductsBy: awardHelper.isMoneyDiscountAward(earningMechanism.productDiscountAward)
        ? earningMechanism.productDiscountAward.awardPer
        : undefined,
    },
  };
};

const transformOfferEarningMechanism = (
  earningMechanism: OfferFragment['earningMechanism'],
  constraints: OfferFragment['constraints'],
) => {
  if (!earningMechanism) {
    return {};
  }

  if (earningMechanismHelper.isVarietyDiscount(earningMechanism)) {
    return transformVarietyDiscount(earningMechanism);
  }

  if (earningMechanismHelper.isTransactionDiscount(earningMechanism)) {
    return transformTransactionDiscount(earningMechanism);
  }

  if (earningMechanismHelper.isProductDiscount(earningMechanism)) {
    return transformProductDiscount(earningMechanism, constraints);
  }

  return {};
};
type TransformOfferToInitialValuesParams = {
  productGroups: ProductGroupFragment[];
  rollbackStackingConfig: RollbackStackingConfig;
};

export const transformOfferToInitialValues =
  ({ productGroups, rollbackStackingConfig }: TransformOfferToInitialValuesParams) =>
  (offer?: OfferFragment): PromotionConfigureFormInitialValues => {
    const values = getOfferConfigureFormInitialValues();

    if (!offer) {
      return values;
    }

    const minimumPurchaseConstraintInitialValues = getMinimumPurchaseConstraintInitialValues(
      offer,
      productGroups,
    );

    return {
      ...values,
      ...getOfferNameAndDescriptionInitialValues(offer),
      ...getOfferFulfillmentPriorityInitialValues(offer),
      customData: transformCustomData(offer.customData),
      earningMechanism: { id: offer.earningMechanism?.id },
      ...transformOfferEarningMechanism(offer.earningMechanism, offer.constraints),
      ...transformOfferToFulfillmentWindowFormValue(offer),
      awardCount: getAwardCountSchemaInitialValues(offer, values.awardCount),
      ...minimumPurchaseConstraintInitialValues,
      paymentMethodConstraint: getPaymentMethodInitialValues(offer),
      fulfillmentChannelConstraint: getFulfillmentChannelInitialValues(offer),
      stackable:
        rollbackStackingConfig === 'PER_OFFER' ? offer.stackable : rollbackStackingConfig === 'ALL',
      activation: getActivationInitialValues(offer, values.activation),
      displayForOrderAhead: offer.displayForOrderAhead ?? false,
      ...transformOfferScanDataConfigurationToInitialValues(offer.scanDataConfiguration),
    };
  };
